<template>
  <div :class="wrapClasses" :style="styles">
    <div class="hidden-columns" style="display: none" ref="hiddenColumns"><slot></slot></div>
    <div :class="classes">
      <div v-if="showSlotHeader"
           ref="title"
           :class="`${prefixTableClass}__title`">
        <slot name="header"></slot>
      </div>
      <div v-if="showHeader" ref="header"
           :class="[prefixTableClass + '__header']"
           @mousewheel="$_onHeaderMouseWheel">
        <t-table-head
          :prefix-table-class="prefixTableClass"
          :style-object="tableStyle"
          :columns="rebuildColumns"
          :select-status="headSelectStatus"
          :columns-width="columnsWidth"
          :is-table-tree="isTableTree"
          :all-ellipsis="allEllipsis"
          :column-head-diagonal="columnHeadDiagonal"
          :first-field-column="firstFieldColumn"
          :data="rebuildData"></t-table-head>
      </div>
      <div v-show="!((!!localeNoDataText && (!data || data.length === 0)) || (!!localeNoFilteredDataText && (!rebuildData || rebuildData.length === 0)))"
           ref="body"
           :class="`${prefixTableClass}__body`"
           :style="bodyStyle"
           @scroll="handleBodyScroll">
        <t-table-body
          ref="tbody"
          :prefix-table-class="prefixTableClass"
          :style-object="tableStyle"
          :columns="isGroup? groupColumn : rebuildColumns"
          :data="rebuildData"
          :all-ellipsis="allEllipsis"
          :tree-data="rebuildTreeData"
          :columns-width="columnsWidth"
          @row-height-correction="rowHeightCorrection"></t-table-body>
      </div>
      <div v-show="hasTips"
           :class="tipCls"
           :style="{ 'height': bodyStyle.height }">
        <template v-if="loadingState">
          <div v-html="localeLoadingDataText"></div>
        </template>
        <template v-else>
          <span v-if="!data || data.length === 0" v-html="localeNoDataText"></span>
          <span v-else v-html="localeNoFilteredDataText"></span>
        </template>
      </div>
      <div v-if="isLeftFixed" :class="[prefixTableClass + '-fixed']" :style="fixedTableStyle">
        <div v-if="showHeader" :class="[prefixTableClass + '-fixed__header']">
          <t-table-head :prefix-table-class="prefixTableClass"
                        :is-group="isGroupColumn"
                        :select-status="headSelectStatus"
                        :style-object="fixedTableStyle"
                        :columns="leftFixedColumns"
                        :columns-width="columnsWidth"
                        :column-head-diagonal="columnHeadDiagonal"
                        :first-field-column="firstFieldColumn"
                        :data="rebuildData"
                        fixed="left"></t-table-head>
        </div>
        <div ref="fixedBody" :class="[prefixTableClass + '-fixed__body']" :style="fixedBodyStyle">
          <t-table-body
            ref="tfixedBody"
            :prefix-table-class="prefixTableClass"
            :style-object="fixedTableStyle"
            :columns="leftFixedColumns"
            :data="rebuildData"
            :all-ellipsis="allEllipsis"
            :columns-width="columnsWidth"
            fixed="left"
            @row-height-correction="rowHeightCorrection"></t-table-body>
        </div>
      </div>
      <div v-if="isRightFixed" :class="[prefixTableClass + '-fixed',prefixTableClass + '-fixed--right']" :style="fixedRightTableStyle">
        <div v-if="showHeader" :class="[prefixTableClass + '-fixed__header']">
          <t-table-head :prefix-table-class="prefixTableClass"
                        :style-object="fixedRightTableStyle"
                        :select-status="headSelectStatus"
                        :columns="rightFixedColumns"
                        :columns-width="columnsWidth"
                        :column-head-diagonal="columnHeadDiagonal"
                        :first-field-column="firstFieldColumn"
                        :data="rebuildData"
                        fixed="right"></t-table-head>
        </div>
        <div ref="fixedRightBody" :class="[prefixTableClass + '-fixed__body']" :style="fixedBodyStyle">
          <t-table-body ref="tfixedRightBody"
                        :prefix-table-class="prefixTableClass"
                        :style-object="fixedRightTableStyle"
                        :columns="rightFixedColumns"
                        :data="rebuildData"
                        :all-ellipsis="allEllipsis"
                        :columns-width="columnsWidth"
                        fixed="right"
                        @row-height-correction="rowHeightCorrection"></t-table-body>
        </div>
      </div>
      <div v-if="showSlotFooter" ref="footer" :class="[prefixTableClass + '__footer']">
        <slot name="footer"></slot>
      </div>
    </div>
  </div>
</template>
<script>
import TTableHead from './table-head.vue'
import TTableBody from './table-body.vue'
import { getStyle, getScrollBarSize } from '../../utils/assist'
import { PREFIX_CLASS } from '../../utils/constants'
import Locale from '../../mixins/locale'
import Mixin from './mixin'
import Emitter from '../../mixins/emitter'
const PREFIX_TABLE_CLASS = `${PREFIX_CLASS}table`
let tableIdSeed = 1
export default {
  name: 'TTable',
  components: { TTableHead, TTableBody },
  mixins: [Mixin, Locale, Emitter],
  props: {
    isGroupColumn: {
      type: Boolean,
      default: false
    }, // 是否多级表头
    data: {
      type: Array,
      default() {
        return []
      }
    },
    columns: {
      type: Array,
      default() {
        return []
      }
    },
    size: {
      type: String,
      validator(value) {
        return ['sm', 'lg', ''].includes(value)
      }
    },
    width: {
      type: [Number, String]
    },
    height: {
      type: [Number, String]
    },
    loading: {
      type: Boolean,
      default: false
    },
    stripe: {
      type: Boolean,
      default: false
    },
    line: {
      type: Boolean,
      default: false
    },
    border: {
      type: Boolean,
      default: false
    },
    allEllipsis: {
      type: Boolean,
      default: false
    },
    treeData: {
      // 是否表格树
      type: Object,
      validator: function(option) {
        return !!option.key
      }
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    highlightRow: {
      type: Boolean,
      default: false
    },
    rowClassName: {
      type: Function,
      default() {
        return ''
      }
    },
    context: {
      type: Object
    },
    noDataText: {
      type: String
    },
    noFilteredDataText: {
      type: String
    },
    columnHeadDiagonal: {
      // 列头斜线
      type: Number,
      default: 0,
      validator: function(value) {
        return [0, 1, 2].includes(value) // 只能添加1条或2条斜线
      }
    },
    keyField: {
      type: [String, Function],
      default: null
    }
  },
  data() {
    return {
      isGroup: false,
      groupColumn: [],
      tableId: '',
      loadingState: false,
      ready: false,
      tableWidth: 0,
      columnsWidth: {},
      prefixTableClass: PREFIX_TABLE_CLASS,
      compiledUids: [],
      rebuildDataMap: {},
      rebuildData: [], // for sort or filter
      rebuildColumns: [],
      showSlotHeader: true,
      showSlotFooter: true,
      bodyHeight: 0,
      bodyRealHeight: 0,
      scrollBarWidth: getScrollBarSize(),
      currentContext: this.context,
      leftFixedColumns: [],
      rightFixedColumns: [],
      selections: [],
      rebuildTreeData: {
        key: '',
        treeIndentSize: 14,
        isOpenNode: true,
        childNodeName: 'children'
      },
      firstFieldColumn: 0, // 第一个字段列序号
      initSort: false, // 初始化是否排序
      initFilter: false, // 初始化是否过滤
      rowHeightArr: [],
      timerResize: null,
      fixedRightWidth: {width: 0},
      fixedLeftWidth: {width: 0},
    }
  },
  computed: {
    localeNoDataText() {
      // 没有数据的文本
      if (this.noDataText === undefined) {
        return this.t('t.table.noDataText')
      } else {
        return this.noDataText
      }
    },
    localeNoFilteredDataText() {
      // 本地过滤没有数据的提示文本
      if (this.noFilteredDataText === undefined) {
        return this.t('t.table.noFilteredDataText')
      } else {
        return this.noFilteredDataText
      }
    },
    localeLoadingDataText() {
      // 加载中提示文本
      if (this.loadingState) {
        return `${this.t('t.table.loading')}`
      }
    },
    wrapClasses() {
      return [
        `${PREFIX_TABLE_CLASS}-wrapper`,
        {
          [`${PREFIX_TABLE_CLASS}--hide`]: !this.ready,
          [`${PREFIX_TABLE_CLASS}--with-header`]: this.showSlotHeader,
          [`${PREFIX_TABLE_CLASS}--with-footer`]: this.showSlotFooter
        }
      ]
    },
    tipCls() {
      return [`${PREFIX_TABLE_CLASS}__tip`]
    },
    hasTips() {
      return (
        this.loadingState ||
        ((!!this.localeNoDataText && (!this.data || this.data.length === 0)) ||
          (!!this.localeNoFilteredDataText &&
            (!this.rebuildData || this.rebuildData.length === 0)))
      )
    },
    classes() {
      return [
        `${PREFIX_TABLE_CLASS}`,
        {
          [`${PREFIX_TABLE_CLASS}--${this.size}`]: !!this.size,
          [`${PREFIX_TABLE_CLASS}--border`]: this.border,
          [`${PREFIX_TABLE_CLASS}--stripe`]: this.stripe,
          [`${PREFIX_TABLE_CLASS}--line`]: this.line,
          [`${PREFIX_TABLE_CLASS}--with-fixed-top`]: !!this.height,
          [`${PREFIX_TABLE_CLASS}--with-fixed-top`]: !!this.width,
          [`${PREFIX_TABLE_CLASS}--tips`]: !!this.hasTips,
          [`${PREFIX_TABLE_CLASS}--loading`]: !!this.loadingState
        }
      ]
    },
    styles() {
      let style = {}
      if (this.height) {
        const height =
          this.isLeftFixed || this.isRightFixed
            ? parseInt(this.height) + this.scrollBarWidth
            : parseInt(this.height)
        style.height = `${height}px`
      }
      if (this.width) style.width = `${this.width}px`
      return style
    },
    tableStyle() {
      let style = {}
      if (this.tableWidth !== 0) {
        let width = ''
        if (this.bodyHeight === 0) {
          width = this.tableWidth
        } else {
          if (this.bodyHeight > this.bodyRealHeight) {
            width = this.tableWidth
          } else {
            width = this.tableWidth - this.scrollBarWidth
          }
        }
        // const width = this.bodyHeight === 0 ? this.tableWidth : this.tableWidth - this.scrollBarWidth;
        style.width = `${width}px`
      }
      return style
    },
    fixedTableStyle() {
      return this.fixedLeftWidth
    },
    fixedRightTableStyle() {
     return this.fixedRightWidth
    },
    bodyStyle() {
      let style = {}
      if (this.bodyHeight !== 0) {
        // add a height to resolve scroll bug when browser has a scrollBar in fixed type and height prop
        const height =
          this.isLeftFixed || this.isRightFixed
            ? this.bodyHeight + this.scrollBarWidth
            : this.bodyHeight
        style.height = `${height}px`
      }
      return style
    },
    fixedBodyStyle() {
      let style = {}
      if (this.bodyHeight !== 0) {
        let height = this.bodyHeight + this.scrollBarWidth - 1

        if (this.width && this.width < this.tableWidth) {
          height = this.bodyHeight
        }
        // style.height = this.scrollBarWidth > 0 ? `${this.bodyHeight}px` : `${this.bodyHeight - 1}px`;
        style.height =
          this.scrollBarWidth > 0 ? `${height}px` : `${height - 1}px`
      }
      return style
    },
    isLeftFixed() {
      return !!this.leftFixedColumns.length
    },
    isRightFixed() {
      return !!this.rightFixedColumns.length
    },
    isTableTree() {
      return (
        this.treeData &&
        this.treeData.key &&
        this.columns.some(cell => cell.key === this.treeData.key)
      )
    },
    headSelectStatus() {
      let status = ''
      const checkedNum = this.selections.length
      if (checkedNum === this.rebuildData.length && checkedNum > 0) {
        status = 'all'
      } else {
        status = checkedNum ? 'indeterminate' : ''
      }
      return status
    }
  },
  watch: {
    data: {
      handler(val) {
        this.$_initData()
        this.$_resize()
        if (val.length > 0 && this.loadingState) {
          this.loadingState = false
        }
      },
      deep: true
    },
    columns: {
      handler() {
        // todo 这里有性能问题，可能是左右固定计算属性影响的
        this.rebuildColumns = this.$_makeColumns()
        this.$_initData()
        this.$_resize()
      },
      deep: true
    },
    height() {
      this.$_fixedHeader()
    },
    loading(val) {
      this.loadingState = val
    }
  },
  created() {
    this.tableId = 'el-table_' + tableIdSeed++;
    this.loadingState = this.loading
    if (!this.context) this.currentContext = this.$parent
    this.showSlotHeader = this.$slots.header !== undefined
    this.showSlotFooter = this.$slots.footer !== undefined
    this.rebuildColumns = this.$_makeColumns()
    this.$_initData()
    this.$on('insert-column', this.$_insertColumn)
  },
  mounted() {
    this.$_resize()
    this.$_fixedHeader()
    this.$nextTick(() => {
      this.ready = true
    })
    window.addEventListener('resize', this.$_resize, false)
    this.$on('on-visible-change', val => {
      if (val) {
        this.$_resize()
        this.$_fixedHeader()
      }
    })
  },
  beforeDestroy() {
    if (this.timerResize !== null) {
      clearTimeout(this.timerResize)
    }
    window.removeEventListener('resize', this.$_resize, false)
  },
  methods: {
    getRowClassName(index) {
      return this.rowClassName(this.data[index], index)
    },
    $_resize() {
      if (this.timerResize !== null) {
        clearTimeout(this.timerResize)
      }
      this.timerResize = setTimeout(() => {
        this.$nextTick(() => {
          // each column set a width
          const allWidth = !this.columns.some(cell => !cell.width)
          if (allWidth && this.columns.length) {
            this.tableWidth = this.columns
              .map(cell => cell.width)
              .reduce((a, b) => a + b)
          } else {
            this.tableWidth = parseInt(getStyle(this.$el, 'width')) - 1
          }
          this.columnsWidth = {}
          this.$nextTick(() => {
            let columnsWidth = {}
            let autoWidthIndex = -1
            if (allWidth) {
              autoWidthIndex = this.rebuildColumns.findIndex(cell => !cell.width)
            } // todo 这行可能有问题
            let leftFixedColumn = []
            let rightFixedColumn = []
            if (this.data.length) {
              const $td = this.$refs.tbody.$el
                .querySelectorAll('tbody tr')[0]
                .querySelectorAll('td')
              let $filterTd = []
              $td.forEach(item => {
                const $tr = item.parentElement || item.parentNode
                // 当表格中有date-picker时会有bug 在此对className做判断
                if ($tr.className.indexOf('table__row') === -1) {
                  return false
                } else {
                  $filterTd.push(item)
                }
              })
              for (let i = 0; i < $filterTd.length; i++) {
                // can not use forEach in Firefox
                const column = this.rebuildColumns[i]
                if(!column) {
                  continue
                }
                let width = parseInt(getStyle($filterTd[i], 'width'))
                if (i === autoWidthIndex) {
                  width = width - 1
                }
                if (column && column.width) width = column.width

                this.rebuildColumns[i]._width = width

                columnsWidth[column._index] = {
                  width: width
                }
                if (column.fixed && column.fixed === 'right') {
                  let fixColumn = Object.assign({}, column)
                  fixColumn.rel = $filterTd[i]
                  rightFixedColumn.push(fixColumn)
                }
                if (column.fixed && column.fixed === 'left') {
                  let fixColumn = Object.assign({}, column)
                  fixColumn.rel = $filterTd[i]
                  leftFixedColumn.push(fixColumn)
                }
              }
              this.columnsWidth = columnsWidth
              // 重新计算固定列
              this.leftFixedColumns = leftFixedColumn
              this.rightFixedColumns = rightFixedColumn
              this.updateFixedColumnStyle()
            }
          })
          // get table real height,for fixed when set height prop,but height < table's height,show scrollBarWidth
          this.bodyRealHeight = parseInt(getStyle(this.$refs.tbody.$el, 'height'))
        })
      }, 300)
    },
    $_highlightCurrentRow(rowIndex) {
      if (!this.highlightRow || this.rebuildData[rowIndex]._isHighlight) return

      let oldIndex = -1
      this.rebuildData.forEach((item, i) => {
        if (item._isHighlight) {
          oldIndex = parseInt(i)
          item._isHighlight = false
        }
      })
      this.rebuildData[rowIndex]._isHighlight = true
      let oldCloneObj = Object.assign({}, this.rebuildData[oldIndex])
      let newCloneObj = Object.assign({}, this.rebuildData[rowIndex])
      const oldData = oldIndex < 0 ? null : oldCloneObj
      this.$emit(
        'on-current-change',
        newCloneObj,
        oldData,
        this.rebuildData[rowIndex]._nativeIndex
      )
    },
    clickRow(index) {
      this.$_highlightCurrentRow(index)
      let data = this.rebuildData.find((element) => {
        return element._index === index
      })
      let cloneData = Object.assign({}, data)
      this.$emit('on-row-click', cloneData, data._nativeIndex)
    },
    dblclickRow(index) {
      this.$_highlightCurrentRow(index)
      let data = this.rebuildData.find((element) => {
        return element._index === index
      })
      let cloneData = Object.assign({}, data)
      this.$emit('on-row-dblclick', cloneData, data._nativeIndex)
    },
    getSelection() {
      let selectionIndexes = []
      this.rebuildData.forEach((item, i) => {
        if (item._checked) selectionIndexes.push(item)
      })
      return selectionIndexes
    },
    toggleSelect(index) {
      let data = this.rebuildData[index]
      data._checked = !data._checked
      this.selections = this.getSelection()
      let cloneObj = Object.assign({}, data)
      this.$emit(
        data._checked ? 'on-select' : 'on-select-cancel',
        this.selections,
        cloneObj,
        data._nativeIndex
      )
      this.$emit('on-selection-change', this.selections)
    },
    toggleExpand(index) {
      let status = !this.rebuildData[index]._isExpanded
      this.rebuildData[index]._isExpanded = status
      let cloneObj = Object.assign({}, this.rebuildData[index])
      this.$emit(
        'on-expand',
        cloneObj,
        status,
        this.rebuildData[index]._nativeIndex
      )
    },
    toggleTreeNode(row, status) {
      let cloneObj = Object.assign({}, row)
      this.$emit('on-treeNode-expand', cloneObj, status)
    },
    /**
     * 可以通过ref 来调用内部方法全选或者清除选取
     */
    setSelectAll(status) {
      this.selectAll(status)
    },
    selectAll(status) {
      for (const data of this.rebuildData) {
        if (!data._isDisabled) {
          this.rebuildData[data._index]._checked = status
        }
      }
      this.selections = this.getSelection()
      if (status) {
        this.$emit('on-select-all', this.selections)
      } else {
        this.$emit('on-select-all-cancel')
      }
      this.$emit('on-selection-change', this.selections)
    },
    $_fixedHeader() {
      if (this.height) {
        this.$nextTick(() => {
          const titleHeight =
            parseInt(getStyle(this.$refs.title, 'height')) || 0
          const headerHeight =
            parseInt(getStyle(this.$refs.header, 'height')) || 0
          const footerHeight =
            parseInt(getStyle(this.$refs.footer, 'height')) || 0
          this.bodyHeight =
            this.height - titleHeight - headerHeight - footerHeight
        })
      } else {
        this.bodyHeight = 0
      }
    },
    $_hideColumnFilter() {
      this.rebuildColumns.forEach(col => {
        col._filterVisible = false
      })
    },
    handleBodyScroll(event) {
      if (this.showHeader) {
        this.$refs.header.scrollLeft = event.target.scrollLeft
      }
      if (this.isLeftFixed) {
        this.$refs.fixedBody.scrollTop = event.target.scrollTop
      }
      if (this.isRightFixed) {
        this.$refs.fixedRightBody.scrollTop = event.target.scrollTop
      }
      this.$_hideColumnFilter()
    },
    $_onHeaderMouseWheel(event) {
      const deltaX = event.deltaX
      const $body = this.$refs.body

      if (deltaX > 0) {
        $body.scrollLeft = $body.scrollLeft + 10
      } else {
        $body.scrollLeft = $body.scrollLeft - 10
      }
    },
    $_sortData(data, type, index) {
      const key = this.rebuildColumns[index].key
      data.sort((a, b) => {
        if (this.rebuildColumns[index].sortMethod) {
          return this.rebuildColumns[index].sortMethod(a[key], b[key], type) ? 1 : -1
        } else {
          if (type === 'asc') {
            return a[key] > b[key] ? 1 : -1
          } else if (type === 'desc') {
            return a[key] < b[key] ? 1 : -1
          }
        }
      })
      return data
    },
    sort(index, type) {
      this.rebuildColumns.forEach(col => {
        col.sortType = 'normal'
      })

      const key = this.rebuildColumns[index].key
      if (this.rebuildColumns[index].sortable !== 'custom') {
        // custom is for remote sort
        if (type === 'normal') {
          if (this.initFilter) {
            this.rebuildData = this.$_makeDataWithFilter()
          } else {
            this.rebuildData = this.$_makeBuildData()
          }
        } else {
          this.rebuildData = this.$_sortData(this.rebuildData, type, index)
        }
      }
      this.rebuildColumns[index].sortType = type

      let cloneObj = Object.assign(
        {},
        this.columns[this.rebuildColumns[index]._index]
      )
      this.$emit('on-sort-change', {
        column: cloneObj,
        key: key,
        order: type
      })
    },
    filterHide(index) {
      // clear checked that not filter now
      if (!this.rebuildColumns[index]._isFiltered) {
        this.rebuildColumns[index]._filterChecked = []
      }
    },
    $_filterData(data, column) {
      return data.filter(row => {
        // 如果定义了远程过滤方法则忽略此方法
        if (typeof column.filterRemote === 'function') return true

        let status = !column._filterChecked.length
        for (let i = 0; i < column._filterChecked.length; i++) {
          status = column.filterMethod(column._filterChecked[i], row)
          if (status) break
        }
        return status
      })
    },
    $_filterOtherData(data, index) {
      let column = this.rebuildColumns[index]
      if (typeof column.filterRemote === 'function') {
        column.filterRemote.call(
          this.$parent,
          column._filterChecked,
          column.key,
          column
        )
      }

      this.rebuildColumns.forEach((col, colIndex) => {
        if (colIndex !== index) {
          data = this.$_filterData(data, col)
        }
      })
      return data
    },
    handleFilter(index) {
      const column = this.rebuildColumns[index]
      let filterData = this.$_makeBuildData()

      // filter others first, after filter this column
      filterData = this.$_filterOtherData(filterData, index)
      this.rebuildData = this.$_filterData(filterData, column)

      this.rebuildColumns[index]._isFiltered = true
      this.rebuildColumns[index]._filterVisible = false
    },
    filterSelect(index, value) {
      this.rebuildColumns[index]._filterChecked = [value]
      this.handleFilter(index)
    },
    filterReset(index) {
      this.rebuildColumns[index]._isFiltered = false
      this.rebuildColumns[index]._filterVisible = false
      this.rebuildColumns[index]._filterChecked = []

      let filterData = this.$_makeBuildData()
      filterData = this.$_filterOtherData(filterData, index)
      this.rebuildData = filterData
    },
    $_makeTreeData(data, indent, nativeIndex) {
      let _dataArr = []
      const _data = data || this.data
      const _childName = this.rebuildTreeData.childNodeName
      _data.forEach((row, index) => {
        let _row = {}
        _row._data = Object.assign({}, row)
        _row._indent = indent || 0
        if (typeof nativeIndex !== 'undefined') {
          // 原本排序
          _row._nativeIndex = nativeIndex
        } else {
          _row._nativeIndex = index
        }
        _row._selfIndex = index
        if (_row._indent > 0 && this.rebuildTreeData.isOpenNode === false) {
          _row._isHidden = true
        }
        _dataArr.push(_row)
        const _child = _row._data[_childName]
        if ((_child && _child.length) || _row._data._hasChild === true) {
          _row._hasChild = true
          _row._isOpenNode = this.rebuildTreeData.isOpenNode
          _dataArr = _dataArr.concat(
            this.$_makeTreeData(
              _row._data.children,
              _row._indent + 1,
              _row._nativeIndex
            )
          )
        }
      })
      return _dataArr
    },
    $_makeBuildData() {
      let data = Object.assign(this.data)
      if (this.isTableTree) {
        data = this.$_makeTreeData()
      }
      data = this.$_makeData(data)
      return data
    },
    $_makeData(data) {
      let formatedData = []
      let dataMap = {}
      data.forEach((row, index) => {
        dataMap[index] = row._data
        if (row._data) {
          dataMap[index] = Object.assign({}, row._data)
          delete row._data
          row = Object.assign({}, row, dataMap[index])
        } else {
          dataMap[index] = Object.assign({}, row)
        }
        let formatedRowData = this.$_makeObjData(row)
        formatedRowData._index = index
        if (row.hasOwnProperty('_nativeIndex')) {
          // 原本排序
          formatedRowData._nativeIndex = row._nativeIndex
        } else {
          formatedRowData._nativeIndex = index
        }
        formatedData.push(formatedRowData)
      })
      this.rebuildDataMap = dataMap
      return formatedData
    },
    $_makeDataWithSort() {
      let data = this.$_makeBuildData()
      let sortType = 'normal'
      let sortIndex = -1
      let isCustom = false

      for (let i = 0; i < this.rebuildColumns.length; i++) {
        if (this.rebuildColumns[i].sortType !== 'normal') {
          sortType = this.rebuildColumns[i].sortType
          sortIndex = i
          isCustom = this.rebuildColumns[i].sortable === 'custom'
          break
        }
      }
      if (sortType !== 'normal' && !isCustom) {
        data = this.$_sortData(data, sortType, sortIndex)
      }
      return data
    },
    $_makeDataWithFilter() {
      let data = this.$_makeBuildData()
      this.rebuildColumns.forEach(col => {
        data = this.$_filterData(data, col)
      })
      return data
    },
    $_makeDataWithSortAndFilter() {
      let data = this.$_makeDataWithSort()
      this.rebuildColumns.forEach(col => {
        data = this.$_filterData(data, col)
      })
      return data
    },
    $_makeObjData(row) {
      let newRow = Object.assign({}, row) // todo 直接替换
      newRow._isDisabled = !!newRow._disabled || false
      newRow._isHighlight = !!newRow._highlight || false
      newRow._isExpanded = !!newRow._isExpanded || false
      newRow._isHidden = !!newRow._isHidden || false
      newRow._checked = !!newRow._checked || false
      // 如果指定了key字段则使用它，否则生成默认的key字段
      if (this.keyField === null) {
        newRow._loopKey = null
      } else if (typeof this.keyField === 'string') {
        newRow._loopKey = row[this.keyField]
      } else { // it is Function type
        newRow._loopKey = this.keyField(row)
        if (!newRow._loopKey) {
          throw new Error('The "keyField" property function must return a valid value!')
        }
      }
      return newRow
    },
    $_makeColumns() {
      let left = []
      let right = []
      let center = []
      let _firstField = -1
      this.initSort = false
      this.initFilter = false
      this.columns.forEach((col, index) => {
        if (this.isTableTree && col.type === 'expand') {
          // 表格树不支持扩展行
          return
        }
        if (!col.type && _firstField === -1) {
          _firstField = index
        }
        let column = Object.assign({}, col)
        column._index = index
        column._width = column.width ? column.width : '' // update in $_resize()
        column.sortType = column.sortType || 'normal'
        column.editable = column.editable || false
        column._filterVisible = false
        column._isFiltered = false
        column._filterChecked = []

        if ('filterMultiple' in column) {
          column._filterMultiple = column.filterMultiple
        } else {
          column._filterMultiple = true
        }
        if ('filteredValue' in column) {
          column._filterChecked = column.filteredValue
          column._isFiltered = true
          this.initFilter = true
        }
        if ('sortType' in column) {
          this.initSort = true
        }
        if (column.fixed && column.fixed === 'left') {
          left.push(column)
        } else if (column.fixed && column.fixed === 'right') {
          right.push(column)
        } else {
          center.push(column)
        }
      })
      this.firstFieldColumn = _firstField
      return left.concat(center).concat(right)
    },
    updateData(_index, _key, row) {
      this.$emit('on-data-edit', row, this.rebuildDataMap[_index], _key)
    },
    updateFixedColumnStyle() {
      this.$nextTick(()=>{
        let style = {}
        let leftWidth = 0
        let rightWidth = 0
        this.leftFixedColumns.forEach(col => {
           let width = parseInt(getStyle(col.rel, 'width')) - 1
            leftWidth+=width
        })
        this.fixedLeftWidth = {width: `${leftWidth}px`}
        this.rightFixedColumns.forEach(col => {
           let width = parseInt(getStyle(col.rel, 'width')) - 1
          rightWidth+=width
        })
        this.fixedRightWidth = {width: `${rightWidth}px`}
      })
    },
    $_initData() {
      let data = []
      if (this.isTableTree) {
        // 表格树不排序
        this.rebuildTreeData = Object.assign(
          this.rebuildTreeData,
          this.treeData
        )
        if (this.initFilter) {
          data = this.$_makeDataWithFilter()
        } else {
          data = this.$_makeBuildData()
        }
      } else {
        if (this.initSort && this.initFilter) {
          data = this.$_makeDataWithSortAndFilter()
        } else if (this.initSort) {
          data = this.$_makeDataWithSort()
        } else if (this.initFilter) {
          data = this.$_makeDataWithFilter()
        } else {
          data = this.$_makeBuildData()
        }
      }
      this.rebuildData = data
      this.selections = this.getSelection()
    },
    rowHeightCorrection(maxHeight, fixed, index) {
      let key = fixed + ''
      if (!this.rowHeightArr[index]) {
        let obj = {}
        obj[key] = maxHeight
        this.rowHeightArr[index] = obj
      } else {
        this.rowHeightArr[index][key] = maxHeight
      }

      let max = this.rowHeightArr[index]['false']
      let orgMax = max
      if (this.isLeftFixed && this.isRightFixed) {
        if (
          this.rowHeightArr[index]['false'] &&
          this.rowHeightArr[index]['left'] &&
          this.rowHeightArr[index]['right']
        ) {
          let max =
            this.rowHeightArr[index]['false'] > this.rowHeightArr[index]['left']
              ? this.rowHeightArr[index]['false']
              : this.rowHeightArr[index]['left']
          max =
            max > this.rowHeightArr[index]['right']
              ? max
              : this.rowHeightArr[index]['right']
          orgMax = this.$refs.tbody.setRowHeight(max, index)
          this.$refs.tfixedBody.setRowHeight(orgMax, index)
          this.$refs.tfixedRightBody.setRowHeight(orgMax, index)
        }
      } else if (!this.isLeftFixed && this.isRightFixed) {
        if (
          this.rowHeightArr[index]['false'] &&
          this.rowHeightArr[index]['right']
        ) {
          let max = this.rowHeightArr[index]['false']
          this.rowHeightArr[index]['false'] >
          this.rowHeightArr[index]['right']
            ? this.rowHeightArr[index]['false']
            : this.rowHeightArr[index]['right']
          orgMax = this.$refs.tbody.setRowHeight(max, index)
          this.$refs.tfixedRightBody.setRowHeight(orgMax, index)
        }
      } else if (this.isLeftFixed && !this.isRightFixed) {
        if (
          this.rowHeightArr[index]['false'] &&
          this.rowHeightArr[index]['left']
        ) {
          let max = this.rowHeightArr[index]['false']
          this.rowHeightArr[index]['false'] > this.rowHeightArr[index]['left']
            ? this.rowHeightArr[index]['false']
            : this.rowHeightArr[index]['left']
          orgMax = this.$refs.tbody.setRowHeight(max, index)
          this.$refs.tfixedBody.setRowHeight(orgMax, index)
        }
      }
    },
    $_insertColumn(value) {
      let column = value.column
      let index = value.index
      let parent = value.parent
      let array = this.rebuildColumns
      if (parent) {
        array = parent.children;
        if (!array) array = parent.children = []
      }

      if (typeof index !== 'undefined') {
        array.splice(index, 0, column);
      } else {
        array.push(column);
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// table.vue?3d131ce0